import { makeEquation, type Equation } from './algebra.ts';

export type TeachingFocus =
  'unknown' | 'equality' | 'both' | 'groups' | 'check' | 'story';
export type TeachingBeat = {
  id: string;
  title: string;
  label: string;
  takeaway: string;
  narration: string;
  equation: Equation;
  before?: Equation;
  annotation: string;
  focus: TeachingFocus;
  audioId: string;
};

// The worked example deliberately differs from the learner's practice problem.
// Completing this explanation records exposure, never mathematical mastery.
export const TEACHING_VERSION = 'equal-groups@1.0.0';
export const teachingBeats: TeachingBeat[] = [
  {
    id: 'meet-the-unknown',
    label: 'Meet the idea',
    title: 'An unknown you can reason about.',
    takeaway: 'x stands for one unknown number. Each x has that same value.',
    narration:
      'Let us build the idea together. This is a worked example: two x plus four equals ten. The letter x stands for a number we do not know yet. Two x means two equal groups of that number. The four means four more units. The tiles help us see the groups; their size does not tell us the value of x. You can pause me or ask a question at any time.',
    equation: makeEquation(2, 4, 0, 10),
    annotation: '2x = x + x',
    focus: 'unknown',
    audioId: 'teach-unknown',
  },
  {
    id: 'meaning-of-equals',
    label: 'See the relationship',
    title: 'Equal means the same value.',
    takeaway: 'The equals sign makes a claim about both sides.',
    narration:
      'Look at the equals sign. It says the expression on the left and the expression on the right have the same value. It does not mean the answer comes next. Our job is to find a value for x that makes this claim true. We can change the appearance of the equation while keeping the same solution, as long as we use an operation that preserves equality.',
    equation: makeEquation(2, 4, 0, 10),
    annotation: 'left value = right value',
    focus: 'equality',
    audioId: 'teach-equality',
  },
  {
    id: 'subtract-from-both',
    label: 'Undo the extra four',
    title: 'One change. Both sides.',
    takeaway: 'Subtracting 4 from both sides leaves 2x = 6.',
    narration:
      'The four extra units are keeping the two x groups from being alone. Subtract four from both entire sides. On the left, plus four and minus four cancel. On the right, ten minus four is six. Now two x equals six. We have changed both expressions by the same amount, so the value of x that works is unchanged. Subtracting from only one side would break that relationship.',
    before: makeEquation(2, 4, 0, 10),
    equation: makeEquation(2, 0, 0, 6),
    annotation: '−4 on the left    −4 on the right',
    focus: 'both',
    audioId: 'teach-subtract',
  },
  {
    id: 'share-equal-groups',
    label: 'Find one group',
    title: 'Two equal groups share six.',
    takeaway: 'Divide both entire sides by 2. Each group is 3.',
    narration:
      'Two equal x groups now have a total of six. To find one group, divide both entire sides by two. Two x divided by two is x, and six divided by two is three. So x equals three. We chose two because there were two equal groups. Division must use a nonzero number. And if a side has several terms, the division must apply to every term on that side.',
    before: makeEquation(2, 0, 0, 6),
    equation: makeEquation(1, 0, 0, 3),
    annotation: '÷2 on the left    ÷2 on the right',
    focus: 'groups',
    audioId: 'teach-divide',
  },
  {
    id: 'check-the-original',
    label: 'Make sure it works',
    title: 'A solution makes the original true.',
    takeaway: 'Put 3 back into the original equation: 2 × 3 + 4 = 10.',
    narration:
      'Finding a number is not the last step. Check it in the original equation. Replace each x with three. Two times three is six, and six plus four is ten. Both sides are ten, so three really is a solution. This check can catch a mistake even when the algebra looked convincing. In your own activity, you will check your answer and explain why your operations worked.',
    equation: makeEquation(0, 10, 0, 10),
    annotation: '2 × 3 + 4 = 6 + 4 = 10',
    focus: 'check',
    audioId: 'teach-check',
  },
  {
    id: 'connect-to-a-story',
    label: 'Use the idea',
    title: 'The same equation can tell a story.',
    takeaway: '$4 to start + $2 per hour × x hours = $10 total.',
    narration:
      'Here is a fictional rental story for our worked example. A scooter costs four dollars to start, then two dollars for every hour. The total is ten dollars. Let x mean the number of hours. Two times x plus four equals ten. Our solution, three, means three hours. Units connect the number to the question. In the next activity you will build a bike rental model with different prices, then try another situation.',
    equation: makeEquation(2, 4, 0, 10),
    annotation: 'x = 3 hours',
    focus: 'story',
    audioId: 'teach-story',
  },
  {
    id: 'your-turn',
    label: 'Take the controls',
    title: 'Now make the reasoning yours.',
    takeaway: 'See the groups → preserve equality → check → explain.',
    narration:
      'You have seen the idea, a worked solution, and an application. Now try a new equation: three x plus six equals eighteen. The numbers have changed, but the reasoning is the same. You choose the operations, check your value in the original, and explain one step. There is no timer. I am here when you need a hint, a replay, or a conversation. Finishing this explanation does not prove understanding; your reasoning will help us see what to practice next.',
    equation: makeEquation(3, 6, 0, 18),
    annotation: 'What would you undo first?',
    focus: 'unknown',
    audioId: 'teach-your-turn',
  },
];

export const teachingLesson = {
  version: TEACHING_VERSION,
  beats: teachingBeats,
};
